import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { focusValidationError } from '../../../../lib/validationFocus';
import { useRecruitmentUsers } from '../../vacancies/useRecruitmentUsers';
import { deliberationApi } from '../deliberationApi';
import type { ApplicantDeliberation } from '../deliberationTypes';
import { useEndorsementForm } from '../useEndorsementForm';
import { sampleEndorsement } from '../utils/sampleDeliberation';
import { EndorsementFields } from './EndorsementFields';

const ID = 'deliberation-endorsement';

/**
 * ENDORSE APPOINTMENT from the applicants list's row menu (product call, 21 Oct 2026) — a POPUP form
 * with the Endorsement panel's own four fields (one hook, one field set), confirmed by the modal's
 * tick. A SHORTLISTED applicant only; the menu entry is disabled otherwise and the server refuses it.
 */
export function EndorsementModal({
  postingId,
  applicantId,
  applicantName,
  positionTitle,
  onClose,
  onSaved,
}: {
  postingId: string;
  applicantId: string;
  applicantName: string;
  positionTitle: string;
  onClose: () => void;
  onSaved: (saved: ApplicantDeliberation) => void;
}) {
  const toast = useToast();
  const form = useEndorsementForm({ postingId, applicantId });
  const accounts = useRecruitmentUsers(deliberationApi.userOptions);
  const [confirmed, setConfirmed] = useState(false);

  async function save() {
    if (!confirmed) return;

    const result = await form.submit();

    if (!result) return;

    if ('failure' in result) {
      focusValidationError(result.failure, { idPrefix: ID });
      return;
    }

    if (result.fileFailed) {
      toast.failure(`Endorsed, but the document was not attached: ${result.fileFailed}.`);
    } else {
      toast.success(`${applicantName} is endorsed for appointment.`);
    }
    onSaved(result.saved);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Endorse appointment"
      size="xl"
      id={`${ID}-modal`}
      autogenerate={{
        onGenerate: () => {
          const sample = sampleEndorsement(accounts.users);
          if (sample.endorsedByUserId) form.setEndorsedBy(sample.endorsedByUserId);
          form.setEndorsedDate(sample.endorsedDate);
          form.setRemarks(sample.remarks);
        },
        disabled: form.saving,
      }}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: form.saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={form.saving}>
            Cancel
          </Button>
          <Button
            id={`${ID}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!confirmed || !form.valid || form.saving}
            loading={form.saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-xs text-slate-500">
          <span className="text-slate-700">{applicantName}</span> is endorsed for appointment to {positionTitle}, and
          listed under Endorsed. The oath taking is then scheduled in their Appointment.
        </p>

        <EndorsementFields
          idPrefix={ID}
          form={form}
          users={accounts.users}
          usersLoading={accounts.loading}
          usersError={accounts.error}
          currentFileName={null}
        />
      </div>
    </Modal>
  );
}
